import { Injectable } from '@angular/core';
import { Credentials } from '@reenstudio/app/core/models/user/user-credentials';
import { User } from '@reenstudio/app/core/models/user/user';
import { UserRole } from '../enum/user-role';
import { Site } from '../models/site/site';
import { Cer } from '../models/cer/cer';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private static readonly TOKEN_KEY = 'user-token';
  private static readonly REFRESH_TOKEN_KEY = 'user-refresh-token';
  private static readonly USER_KEY = 'user';
  private static readonly CREDENTIALS_KEY = 'credentials';
  private static readonly USERID = 'sub';
  private static readonly NAME = 'name';
  private static readonly EMAIL = 'email';
  private static readonly LEVEL = 'level';
  private static readonly CER_ID = 'cer_id';
  private static readonly SITE_ID = 'site_id';
  private static readonly SITES = 'sites';
  private static readonly CERS = 'cers';

  constructor() {}

  setToken(token: string) {
    this.setItem(StorageService.TOKEN_KEY, token);
  }

  getToken(): string | null {
    return this.getItem<string>(StorageService.TOKEN_KEY);
  }

  setRefreshToken(token: string) {
    this.setItem(StorageService.REFRESH_TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return this.getItem<string>(StorageService.REFRESH_TOKEN_KEY);
  }

  getLoginData() {
    return this.getItem<Credentials>(StorageService.CREDENTIALS_KEY);
  }

  setLoginData(data: Credentials) {
    this.setItem(StorageService.CREDENTIALS_KEY, data);
  }

  removeLoginData() {
    localStorage.removeItem(StorageService.CREDENTIALS_KEY);
  }

  getUser() {
    return this.getItem<User>(StorageService.USER_KEY);
  }

  setUser(user: User) {
    this.setItem(StorageService.USER_KEY, user);
  }

  removeToken() {
    localStorage.removeItem(StorageService.TOKEN_KEY);
  }

  getUserId() {
    return this.getItem<string>(StorageService.USERID);
  }

  setUserId(user: string) {
    this.setItem(StorageService.USERID, user);
  }

  getName() {
    return this.getItem<string>(StorageService.NAME);
  }

  setName(user: string) {
    this.setItem(StorageService.NAME, user);
  }

  getEmail() {
    return this.getItem<string>(StorageService.EMAIL);
  }

  setEmail(user: string) {
    this.setItem(StorageService.EMAIL, user);
  }

  getLevel() {
    return this.getItem<UserRole>(StorageService.LEVEL);
  }

  setLevel(user: string) {
    this.setItem(StorageService.LEVEL, user);
  }

  getCer() {
    return this.getItem<number>(StorageService.CER_ID);
  }

  setCer(cerId: number) {
    this.setItem(StorageService.CER_ID, cerId);
  }

  getCers() {
    return this.getItem<Cer[]>(StorageService.CERS);
  }

  setCers(cers: Cer[]) {
    this.setItem(StorageService.CERS, cers);
  }

  getSite() {
    return this.getItem<number>(StorageService.SITE_ID);
  }

  setSite(cerId: number) {
    this.setItem(StorageService.SITE_ID, cerId);
  }

  getSites() {
    return this.getItem<Site[]>(StorageService.SITES);
  }

  setSites(sites: Site[]) {
    this.setItem(StorageService.SITES, sites);
  }

  private setItem(key: string, value: any): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  private getItem<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Error retrieving item from localStorage: ${error}`);
      return null;
    }
  }

  clear() {
    localStorage.clear();
  }
}
